import { formatDeadline } from './offerDeadline';

/**
 * The struck-through "was / now" price for a single package on the pricelist —
 * the PERCENTAGE portion of the offer only.
 *
 * The percentage is linear and per-package (`cart.price * value/100`), so it
 * matches what checkout charges to the cent. A FLAT amount (the `$X credit` in a
 * PERCENTAGE_AND_FLAT_RATE offer) is taken ONCE off the whole order, so it is NOT
 * baked into the per-card price here — folding it into each card would make the
 * cards sum to LESS than checkout charges when the cart holds more than one item.
 * Instead the flat part is surfaced separately (see `orderCredit`) as an
 * order-level "$X credit at checkout" line. So a combined %+flat offer shows a
 * real per-card strike-through (the %) AND the credit, honestly.
 *
 * Returns null only for offers with NO percentage part (pure FLAT_RATE /
 * FREE_SHIPPING / VOLUME_DISCOUNT_FOR_PACKAGE) — those have no honest per-card
 * strike-through; their value still rides the banner + checkout Discount line.
 *
 * Keep this the ONLY place the client computes a discounted price. If you find
 * yourself replicating a coupon calculation elsewhere, call this instead — the
 * one-implementation rule is exactly what kept the two server discount paths from
 * drifting, and it applies on the client too.
 *
 * @param {number|string} price  regular package price
 * @param {object|null} coupon   resolved offer coupon ({ couponType, value, flatValue })
 * @returns {number|null} discounted price (>= 0), or null if there's no percentage part
 */
// Coupon types with a straight-percentage part (in `value`) off the package price.
// The *AND_FLAT_RATE variants ALSO carry a flat order credit in `flatValue` — the
// percentage still shows a clean per-card was/now; the flat rides orderCredit().
const PERCENT_TYPES = [
  'PERCENTAGE',
  'PERCENTAGE_WITH_FREE_SHIPPING',
  'PERCENTAGE_AND_FLAT_RATE',
  'PERCENTAGE_AND_FLAT_RATE_WITH_FREE_SHIPPING',
  // 20% off everything (incl. any 2nd announcement bundle); one announcement
  // bundle is separately free -- see grantsFreeAnnouncements / the add-ons card.
  'PERCENTAGE_WITH_FREE_ANNOUNCEMENTS',
  // The percentage comes off the package price in full; the retouching add-on is
  // separately waived, which is order-level and so not in the per-card was/now --
  // see grantsFreeRetouching.
  'PERCENTAGE_WITH_FREE_RETOUCHING',
];

// True when the offer makes one announcement bundle free (the announcement cards
// on the add-ons page show it "Free" instead of the %-off price). The server's
// PERCENTAGE_WITH_FREE_ANNOUNCEMENTS branch is the authority on the charge.
export function grantsFreeAnnouncements(coupon) {
  return coupon?.couponType === 'PERCENTAGE_WITH_FREE_ANNOUNCEMENTS';
}

// True when the offer waives the retouching add-on charge -- the retouching-only
// codes, and PERCENTAGE_WITH_FREE_RETOUCHING which waives it on top of a percentage.
// The substring match mirrors the server's Coupon#waives_retouching? exactly; every
// type naming retouching waives it.
//
// THE ONE client interpreter, for describing an offer the customer has NOT applied
// yet (the pricelist banner, offerLine). A screen asking "is retouching free in MY
// cart" must instead read `cart.waivesRetouching`, the server-computed flag: only
// that knows the coupon is actually attached.
export function grantsFreeRetouching(coupon) {
  return !!coupon?.couponType?.includes('RETOUCHING');
}

// Is this cart EXTRA the retouching add-on? Mirrors the server's
// Extra#is_retouching? exactly -- a case-insensitive 'retouch' in the name -- which
// is what OrderPackage#includes_retouching? and so Cart#retouching_charges_total
// key off when deciding what the coupon waives. Matching on the name rather than an
// id is the app-wide convention for extras (the same record is duplicated per
// pricelist, so there is no single id to compare).
//
// THE one client implementation. Paired with the cart's server-computed
// `waivesRetouching` flag, this is what lets the cart line strike out a retouching
// charge the coupon is paying for.
export function isRetouchingExtra(extra) {
  return !!extra?.name?.toLowerCase().includes('retouch');
}

// True when the offer gives 2 free photo magnets with any package (the
// elementary days 15-45 tier, issue #5218). THE ONE client interpreter of that
// tier: the banner, the package-card badge/row/magnet slot, the Buy All card
// ribbon, the magnet-bonus page gate and the "FREE with any package" label all
// ask this. Nothing else may inspect the coupon type for it. The server's
// FREE_MAGNETS_WITH_FREE_SHIPPING branch is the authority on the charge.
export function grantsFreeMagnets(coupon) {
  return coupon?.couponType === 'FREE_MAGNETS_WITH_FREE_SHIPPING';
}

// The magnet tier's own line, e.g. "2 FREE photo magnets with any package +
// free shipping — ends Friday, October 24". No coupon code: the tier is
// auto-applied, and quoting a code would send parents hunting for a box.
export function freeMagnetsLine(coupon) {
  if (!grantsFreeMagnets(coupon)) return null;
  const deadline = formatDeadline(coupon.expiresOn);
  return `2 FREE photo magnets with any package + free shipping${deadline ? ` — ends ${deadline}` : ''}`;
}

// ---- Free item of your choice (FREE_ADDON_CHOICE, #5747) --------------------
//
// True when the offer gives ONE add-on of the parent's choice with every
// qualifying package, from the set the coupon names (`freeAddons`, inline on
// the offer). THE ONE client interpreter of that type: the banner, the
// package-card badge/row/thumbnail, the Buy All card ribbon, the picker gate
// and the cart summary all ask this. Nothing else may inspect the coupon
// type for it. The server's FREE_ADDON_CHOICE branch is the authority on the
// charge, and a coupon of that type with an EMPTY set grants nothing -- there
// is nothing to pick -- so the surfaces stay quiet rather than promise a
// blank.
export function freeAddonsOf(coupon) {
  return Array.isArray(coupon?.freeAddons) ? coupon.freeAddons : [];
}

export function grantsFreeItem(coupon) {
  return coupon?.couponType === 'FREE_ADDON_CHOICE' && freeAddonsOf(coupon).length > 0;
}

// What to CALL the free item. The name typed on the coupon screen
// (`freeItemLabel`, "dry erase magnet") when there is one; otherwise derived
// from the set so the copy is never hard-coded to one sale: one add-on is its
// own name; several share the words they have in common from the left
// ("Weekly Dry Erase Magnet - School" + "- Groovy" -> "Weekly Dry Erase
// Magnet"), with a trailing separator trimmed. Sets with nothing in common
// fall back to "gift" -- which is what a typed name is for.
export function freeItemLabel(coupon) {
  const typed = (coupon?.freeItemLabel || '').trim();
  if (typed) return typed;
  const names = freeAddonsOf(coupon).map((a) => (a?.name || '').trim()).filter(Boolean);
  if (names.length === 0) return 'gift';
  if (names.length === 1) return names[0];

  const words = names.map((n) => n.split(/\s+/));
  const common = [];
  for (let i = 0; i < words[0].length; i += 1) {
    const w = words[0][i];
    if (!words.every((ws) => ws[i] === w)) break;
    common.push(w);
  }
  while (common.length && /^[-–—:|(]+$/.test(common[common.length - 1])) common.pop();
  return common.length ? common.join(' ') : 'gift';
}

// The one-word noun for the badge ("+ FREE MAGNET"), from the set's
// gifts-page category -- the label above can be five words long and a ribbon
// has room for one. Mixed or unknown categories read "GIFT".
const CATEGORY_NOUNS = {
  magnets: 'magnet', gifts: 'gift', prints: 'print', announcements: 'announcement', digital: 'download',
};

export function freeItemNoun(coupon) {
  const categories = [...new Set(freeAddonsOf(coupon).map((a) => a?.giftCategory).filter(Boolean))];
  return (categories.length === 1 && CATEGORY_NOUNS[categories[0]]) || 'gift';
}

// The offer's own banner line, e.g. "A FREE dry erase magnet with any package
// — ends Saturday, October 25". No design count (Katie, 2026-10-18) and no
// coupon code: the offer auto-applies, and quoting a code sends parents
// hunting for a box.
export function freeItemLine(coupon) {
  if (!grantsFreeItem(coupon)) return null;
  const deadline = formatDeadline(coupon.expiresOn);
  return `A FREE ${freeItemLabel(coupon)} with any package${deadline ? ` — ends ${deadline}` : ''}`;
}

// Coupon types that carry a flat dollar credit. For the combined *AND_FLAT_RATE
// types the amount is in `flatValue`; for pure FLAT_RATE it's in `value`.
const FLAT_TYPES = [
  'PERCENTAGE_AND_FLAT_RATE',
  'PERCENTAGE_AND_FLAT_RATE_WITH_FREE_SHIPPING',
  'FLAT_RATE',
  'FLAT_RATE_WITH_FREE_SHIPPING',
];

export default function discountedPrice(price, coupon) {
  if (!coupon || !PERCENT_TYPES.includes(coupon.couponType)) return null;

  const base = Number(price);
  const pct = Number(coupon.value);
  if (Number.isNaN(base) || Number.isNaN(pct) || pct <= 0) return null;

  return Math.max(0, base * (1 - pct / 100));
}

/**
 * The order-level flat credit (a whole-dollar amount taken once off the order),
 * for the "$X credit at checkout" line. Null when the offer has no flat part.
 * @returns {number|null} the credit dollars, or null
 */
export function orderCredit(coupon) {
  if (!coupon || !FLAT_TYPES.includes(coupon.couponType)) return null;

  const isCombined = coupon.couponType.startsWith('PERCENTAGE_AND_FLAT_RATE');
  const amount = Number(isCombined ? coupon.flatValue : coupon.value);
  if (Number.isNaN(amount) || amount <= 0) return null;

  return amount;
}

/**
 * The one-line promo description, e.g. "20% off, $20 credit and free shipping.
 * Use coupon CLASSOF27". Derived from the coupon so the copy always matches the
 * actual discount + code. Returns null if there's nothing quantifiable to state.
 */
export function offerLine(coupon) {
  if (!coupon) return null;
  if (grantsFreeMagnets(coupon)) return freeMagnetsLine(coupon);
  if (grantsFreeItem(coupon)) return freeItemLine(coupon);

  const bits = [];
  if (PERCENT_TYPES.includes(coupon.couponType)) {
    const pct = Number(coupon.value);
    if (!Number.isNaN(pct) && pct > 0) bits.push(`${pct}% off`);
  }
  const credit = orderCredit(coupon);
  if (credit) bits.push(`$${credit} credit`);
  if (grantsFreeAnnouncements(coupon)) bits.push('free announcements');
  if (grantsFreeRetouching(coupon)) bits.push('free retouching');
  if (bits.length === 0) return null;

  if (/FREE_SHIPPING/.test(coupon.couponType)) bits.push('free shipping');

  // "a and b" for two, "a, b and c" for three (Oxford-less, reads naturally).
  const list = bits.length > 1
    ? `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}`
    : bits[0];
  return `${list}. Use coupon ${coupon.code}`;
}

/**
 * Format a price for a pricelist card — whole dollars read cleaner, cents only when
 * they exist. Shared so senior (PackageChooser) and elementary (PackageDisplay) format
 * the "was / now" identically. Returns null for a non-numeric input.
 */
export function money(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}
